import type { Placement } from '@floating-ui/react';
import {
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import React from 'react';

import styles from './tooltip.module.scss';

export function Tooltip({
  children,
  tooltip,
  ...options
}: {
  children: React.ReactElement;
  tooltip: React.ReactNode;
} & TooltipOptions) {
  const _tooltip = useTooltip(options);
  if (!tooltip) return null;
  return (
    <TooltipContext.Provider value={_tooltip}>
      <TooltipTrigger tooltip={tooltip}>{children}</TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </TooltipContext.Provider>
  );
}

interface TooltipOptions {
  placement?: Placement;
}
type ContextType = ReturnType<typeof useTooltip> | null;
const TooltipContext = React.createContext<ContextType>(null);
const useTooltipContext = () => {
  const context = React.useContext(TooltipContext);

  if (context == null) {
    throw new Error('Tooltip components must be wrapped in <Tooltip />');
  }

  return context;
};

function useTooltip({ placement = 'top' }: TooltipOptions = {}) {
  const [open, setOpen] = React.useState(false);

  /* eslint-disable react-hooks/refs */
  // eslint identifies false positive for the 'refs' returned
  // by useFloating (https://github.com/floating-ui/floating-ui/discussions/3405)
  // If react ref objects are ever used in this file, refine
  // when it is turned off and on to protect the use of those refs
  const data = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(5),
      flip({
        crossAxis: placement.includes('-'),
        fallbackAxisSideDirection: 'start',
        padding: 5,
      }),
      shift({ padding: 5 }),
    ],
  });

  const context = data.context;

  const hover = useHover(context, {
    move: false,
    enabled: true,
  });
  const focus = useFocus(context, {
    enabled: true,
  });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });

  const interactions = useInteractions([hover, focus, dismiss, role]);

  return React.useMemo(
    () => ({
      open,
      setOpen,
      ...interactions,
      ...data,
    }),
    [open, setOpen, interactions, data]
  );
}

function TooltipTrigger({
  children,
  tooltip,
}: {
  children: React.ReactElement;
  tooltip: React.ReactNode;
}) {
  const context = useTooltipContext();

  return React.cloneElement(
    children,
    context.getReferenceProps({
      ref: context.refs.setReference,
      ...children.props,
      'aria-hidden': 'false',
      title: tooltip,
      'data-state': context.open ? 'open' : 'closed',
    })
  );
}

function TooltipContent({ children }: { children: React.ReactNode }) {
  const context = useTooltipContext();

  if (!context.open) return null;

  return (
    <FloatingPortal>
      <div
        ref={context.refs.setFloating}
        style={{
          ...context.floatingStyles,
        }}
        className={styles.tooltip}
        {...context.getFloatingProps()}
      >
        {children}
      </div>
    </FloatingPortal>
  );
}
